// Schnelleingabe (Projektplan 10.4a): zweites Homescreen-Icon fuer den
// Kaltstart "deutlich unter einer Sekunde". Bewusst schlank gegenueber dem
// Hauptscreen - keine Drawer, kein Karten-Link, kein Blaettern zwischen
// mehreren Laendern, nur Eingabe, bester Treffer, Stern. Wer mehr braucht
// (mehrere Treffer durchblaettern, ein anderes Land), geht auf den
// Hauptscreen.
//
// Traegt trotzdem die volle React-App und ihr Bundle mit, keinen eigenen
// Vite-Entry-Punkt: Nach der ersten Installation liegen App-Huelle und
// Kennzeichen-Bundle laengst im Service-Worker-Cache respektive in
// IndexedDB (Phase 9, lib/offlineBundle.ts), ein zweiter Kaltstart lauft
// von dort. Ein echter zweiter Build waere eine eigene Baustelle fuer
// denselben Effekt.

import { useMemo, useState } from "react";
import { Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useBundle, type Land } from "../lib/bundle.js";
import { aktiveSammlung } from "../lib/collections.js";
import { useEintragDetails, useEintragSetzenMitSpiegel, useEntries } from "../lib/entries.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import { findeTreffer, type Treffer } from "../lib/treffer.js";
import type { Kennzeichen } from "../lib/bundle.js";

const LEERE_LAENDER: Land[] = [];
const LEERE_KENNZEICHEN: Kennzeichen[] = [];

function beschriftung(treffer: Treffer): string {
  if (treffer.kennzeichen.regionNames.length > 0) return treffer.kennzeichen.regionNames.join(" / ");
  return treffer.kennzeichen.derivation ?? treffer.kennzeichen.code;
}

interface Settings {
  countryOrder?: string[];
  countryVisibility?: Record<string, { list: boolean; search: boolean }>;
  activeCollectionId?: number;
  activeCountry?: string;
  historischeAusblenden?: boolean;
  diplomatenAnzeigen?: boolean;
  globaleSuche?: boolean;
  spiegleInEigene?: boolean;
}

export function Schnell({ nutzer, sammlungen }: { nutzer: Nutzer; sammlungen: Sammlung[] }) {
  const [eingabe, setEingabe] = useState("");
  const bundle = useBundle();
  const settings = (nutzer.settings ?? {}) as Settings;
  const sammlung = aktiveSammlung(sammlungen, settings.activeCollectionId);
  const eintraege = useEntries(sammlung?.id);
  const eintragDetails = useEintragDetails(sammlung?.id);
  // Spiegeln gilt auch hier: die Schnelleingabe ist derselbe Stern wie auf
  // dem Hauptscreen, nur schlanker drumherum. Sie hat frueher still nur in
  // die aktive Sammlung geschrieben.
  const { setzen: eintragSetzen, spiegeln, isPending } = useEintragSetzenMitSpiegel(
    sammlung,
    sammlungen,
    settings.spiegleInEigene,
  );

  const laender = bundle.data?.laender ?? LEERE_LAENDER;
  const kennzeichen = bundle.data?.kennzeichen ?? LEERE_KENNZEICHEN;
  const karte = eintraege.data ?? new Map<number, number>();
  const nutzerReihenfolge = useMemo(() => settings.countryOrder ?? [], [settings.countryOrder]);
  const sichtbarkeit = useMemo(() => settings.countryVisibility ?? {}, [settings.countryVisibility]);

  // Dieselben Einstellungen-Schalter wie auf dem Hauptscreen (lib/treffer.ts),
  // damit "Diplomatenkennzeichen anzeigen" & Co. nicht vom gewaehlten
  // Bildschirm abhaengen.
  const trefferFilter = useMemo(
    () => ({
      historischeAusblenden: settings.historischeAusblenden ?? false,
      diplomatenAnzeigen: settings.diplomatenAnzeigen ?? true,
      nurLand: (settings.globaleSuche ?? true) ? undefined : settings.activeCountry,
    }),
    [settings.historischeAusblenden, settings.diplomatenAnzeigen, settings.globaleSuche, settings.activeCountry],
  );

  const treffer = useMemo(
    () => findeTreffer(eingabe, kennzeichen, laender, nutzerReihenfolge, sichtbarkeit, trefferFilter),
    [eingabe, kennzeichen, laender, nutzerReihenfolge, sichtbarkeit, trefferFilter],
  );
  const bester = treffer[0];
  const gesammelt = bester ? karte.has(bester.kennzeichen.id) : false;
  const eigenerEintrag = bester ? eintragDetails.get(bester.kennzeichen.id) : undefined;

  function eintragen() {
    if (!bester || gesammelt) return;
    eintragSetzen({ plateCodeId: bester.kennzeichen.id, punkte: bester.kennzeichen.points });
  }

  return (
    <div className="flex min-h-full flex-col items-center gap-4 bg-flaeche-grund p-4 pt-10 font-sans text-neutral-900">
      <input
        autoFocus
        inputMode="text"
        value={eingabe}
        onChange={(e) => setEingabe(e.target.value.toUpperCase())}
        placeholder="Kennzeichen"
        // Deutlich groesser als das gewoehnliche Eingabefeld: fuer den einen
        // Zweck dieser Seite darf die Tastatur ruhig den halben Bildschirm
        // einnehmen, nichts anderes lenkt hier ab.
        className="w-full max-w-xs rounded border border-neutral-300 px-4 py-4 text-center text-3xl uppercase tracking-widest outline-none focus:border-akzent"
      />

      <div className="flex min-h-32 w-full max-w-xs flex-col items-center justify-center text-center">
        {bester ? (
          <>
            <p className="text-2xl font-medium">{bester.kennzeichen.code}</p>
            <p className="text-neutral-600">{beschriftung(bester)}</p>
            {eigenerEintrag ? (
              <p className="mt-1 text-sm italic text-neutral-500">Schon in deiner Sammlung</p>
            ) : (
              <p className="mt-1 text-sm text-neutral-500">{bester.kennzeichen.points} Punkte</p>
            )}
            <button
              type="button"
              onClick={eintragen}
              disabled={gesammelt || isPending}
              aria-label="Zur Sammlung hinzufügen"
              className="mt-3 flex min-h-16 min-w-16 items-center justify-center rounded-full bg-akzent text-white disabled:bg-neutral-300"
            >
              <Star size={32} fill={gesammelt ? "currentColor" : "none"} />
            </button>
            {treffer.length > 1 && (
              <p className="mt-2 text-xs text-neutral-400">
                +{treffer.length - 1} weitere{treffer.length - 1 === 1 ? "r" : ""} Treffer - für alle die
                Hauptseite öffnen
              </p>
            )}
          </>
        ) : (
          eingabe.length > 0 && <p className="text-sm text-neutral-500">Kein Treffer</p>
        )}
      </div>

      <p className="text-xs text-neutral-400">
        {spiegeln ? `${sammlung?.name} und deine eigene Sammlung` : (sammlung?.name ?? "")}
      </p>

      <Link to="/" className="mt-4 text-sm text-neutral-500 underline">
        Zur Hauptseite
      </Link>
    </div>
  );
}
